import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

export interface AssetActionModalProps {
  show: boolean;
  assetType: 'nps' | 'ppf' | 'epf' | 'gold' | 'lic' | 'real_estate' | 'fixed_deposit' | 'bank_account' | null;

  mode: 'import' | 'manual';
  onHide: () => void;
  onSubmit?: (data: any) => void;
  policyToEdit?: any;
  propertyToEdit?: any;
  fdToEdit?: any;
  bankAccountToEdit?: any;
  ppfAccountToEdit?: any;
  epfMemberToEdit?: any;
  initialData?: any;
}

export const AssetActionModal: React.FC<AssetActionModalProps> = ({
  show,
  assetType,
  mode,
  onHide,
  onSubmit,
  policyToEdit,
  propertyToEdit,
  fdToEdit,
  bankAccountToEdit,
  ppfAccountToEdit,
  epfMemberToEdit,
  initialData
}) => {
  const { token } = useAuth();

  // Common states
  const [submitting, setSubmitting] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Form states
  // NPS/EPF file imports
  const [file, setFile] = useState<File | null>(null);
  const [npsAccountDestination, setNpsAccountDestination] = useState('');
  const [npsAccountName, setNpsAccountName] = useState('');
  const [npsHolderName, setNpsHolderName] = useState('');
  const [npsPdfPassword, setNpsPdfPassword] = useState('');
  const [epfEntryMode, setEpfEntryMode] = useState<'import' | 'manual'>('import');

  // PPF manual fields
  const [ppfAccountName, setPpfAccountName] = useState('');
  const [ppfHolderName, setPpfHolderName] = useState('');
  const [ppfAccountReference, setPpfAccountReference] = useState('');
  const [ppfProvider, setPpfProvider] = useState('');
  const [ppfBalance, setPpfBalance] = useState('');
  const [ppfInterest, setPpfInterest] = useState('');
  const [ppfContribution, setPpfContribution] = useState('');
  const [ppfBalanceDate, setPpfBalanceDate] = useState(new Date().toISOString().split('T')[0]);

  // EPF manual fields
  const [epfMemberName, setEpfMemberName] = useState('');
  const [epfUan, setEpfUan] = useState('');
  const [epfMemberId, setEpfMemberId] = useState('');
  const [epfEmployeeBalance, setEpfEmployeeBalance] = useState('');
  const [epfEmployerBalance, setEpfEmployerBalance] = useState('');
  const [epfPensionBalance, setEpfPensionBalance] = useState('');
  const [epfInterest, setEpfInterest] = useState('');
  const [epfContribution, setEpfContribution] = useState('');
  const [epfBalanceDate, setEpfBalanceDate] = useState(new Date().toISOString().split('T')[0]);

  // Gold manual fields
  const [goldHoldingType, setGoldHoldingType] = useState<'digital' | 'physical'>('digital');
  const [goldGrams, setGoldGrams] = useState('');
  const [goldPrice, setGoldPrice] = useState('');
  const [goldCurrentPrice, setGoldCurrentPrice] = useState('');
  const [goldDate, setGoldDate] = useState(new Date().toISOString().split('T')[0]);
  const [goldCashFlowKind, setGoldCashFlowKind] = useState<'balance_snapshot' | 'actual_purchase'>('balance_snapshot');

  // LIC manual fields
  const [licPolicyName, setLicPolicyName] = useState('');
  const [licPolicyNumber, setLicPolicyNumber] = useState('');
  const [licSumAssured, setLicSumAssured] = useState('');
  const [licPremiumAmount, setLicPremiumAmount] = useState('');
  const [licPremiumFrequency, setLicPremiumFrequency] = useState('yearly');
  const [licStartDate, setLicStartDate] = useState('');
  const [licNextDueDate, setLicNextDueDate] = useState('');
  const [licMaturityDate, setLicMaturityDate] = useState('');
  const [licPremiumsPaidCount, setLicPremiumsPaidCount] = useState('1');
  const [licCurrentValue, setLicCurrentValue] = useState('');
  const [licAccruedBonus, setLicAccruedBonus] = useState('');

  // Real Estate manual fields
  const [realEstatePropertyName, setRealEstatePropertyName] = useState('');
  const [realEstateLocation, setRealEstateLocation] = useState('');
  const [realEstatePropertyCost, setRealEstatePropertyCost] = useState('');
  const [realEstateCurrentValue, setRealEstateCurrentValue] = useState('');
  const [realEstateLoanOutstanding, setRealEstateLoanOutstanding] = useState('');
  const [realEstateRentalIncome, setRealEstateRentalIncome] = useState('');

  // Fixed Deposit manual fields
  const [fdName, setFdName] = useState('');
  const [fdBankName, setFdBankName] = useState('');
  const [fdPrincipal, setFdPrincipal] = useState('');
  const [fdInterestRate, setFdInterestRate] = useState('');
  const [fdStartDate, setFdStartDate] = useState('');
  const [fdMaturityDate, setFdMaturityDate] = useState('');
  const [fdCurrentValue, setFdCurrentValue] = useState('');

  // Bank Account manual fields
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccountType, setBankAccountType] = useState('savings');
  const [bankBalance, setBankBalance] = useState('');
  const [bankAccountReference, setBankAccountReference] = useState('');
  const [bankLastUpdated, setBankLastUpdated] = useState(new Date().toISOString().split('T')[0]);


  // Reset form when modal visibility changes
  useEffect(() => {
    if (show) {
      // Reset form defaults
      setFile(null);
      const existingNpsAccounts = Array.isArray(initialData?.metadata?.accounts)
        ? initialData.metadata.accounts
        : [];
      setNpsAccountDestination(existingNpsAccounts.length > 0 ? '' : '__new__');
      setNpsAccountName('');
      setNpsHolderName('');
      setNpsPdfPassword('');
      setEpfEntryMode(mode === 'import' ? 'import' : 'manual');
      setPpfAccountName('');
      setPpfHolderName('');
      setPpfAccountReference('');
      setPpfProvider('');
      setPpfBalance('');
      setPpfInterest('');
      setPpfContribution('');
      setPpfBalanceDate(new Date().toISOString().split('T')[0]);
      setEpfMemberName('');
      setEpfUan('');
      setEpfMemberId('');
      setEpfEmployeeBalance('');
      setEpfEmployerBalance('');
      setEpfPensionBalance('');
      setEpfInterest('');
      setEpfContribution('');
      setEpfBalanceDate(new Date().toISOString().split('T')[0]);
      setGoldHoldingType('digital');
      setGoldGrams('');
      setGoldPrice('');
      setGoldCurrentPrice('');
      setGoldDate(new Date().toISOString().split('T')[0]);
      setGoldCashFlowKind('balance_snapshot');

      if (assetType === 'ppf') {
        const ppfSource = ppfAccountToEdit || null;
        if (ppfSource) {
          setPpfAccountName(ppfSource.account_name || '');
          setPpfHolderName(ppfSource.holder_name || '');
          setPpfAccountReference(ppfSource.account_reference || '');
          setPpfProvider(ppfSource.provider || '');
          setPpfBalance(ppfSource.current_balance ? ppfSource.current_balance.toString() : '');
          setPpfInterest(ppfSource.interest_rate ? ppfSource.interest_rate.toString() : '');
          setPpfContribution(ppfSource.monthly_contribution ? ppfSource.monthly_contribution.toString() : '');
          setPpfBalanceDate(ppfSource.valuation_date || new Date().toISOString().split('T')[0]);
        }
      }

      if (assetType === 'epf') {
        const epfSource = epfMemberToEdit || null;
        if (epfSource) {
          setEpfMemberName(epfSource.member_name || '');
          setEpfUan(epfSource.uan || '');
          setEpfMemberId(epfSource.member_id || epfSource.account_reference || '');
          setEpfEmployeeBalance(epfSource.employee_share ? epfSource.employee_share.toString() : '');
          setEpfEmployerBalance(epfSource.employer_share ? epfSource.employer_share.toString() : '');
          setEpfPensionBalance(epfSource.pension_share ? epfSource.pension_share.toString() : '');
          setEpfInterest(epfSource.interest_rate ? epfSource.interest_rate.toString() : '');
          setEpfContribution(epfSource.monthly_contribution ? epfSource.monthly_contribution.toString() : '');
          setEpfBalanceDate(epfSource.valuation_date || new Date().toISOString().split('T')[0]);
        } else if (initialData) {
          setEpfInterest(initialData.interest_rate ? initialData.interest_rate.toString() : (initialData.metadata?.interest_rate ? initialData.metadata.interest_rate.toString() : ''));
        }
      }

      if (assetType === 'gold' && initialData) {
        setGoldHoldingType(initialData.metadata?.holding_type || 'digital');
        setGoldGrams(initialData.metadata?.grams ? initialData.metadata.grams.toString() : (initialData.metadata?.quantity ? initialData.metadata.quantity.toString() : ''));
        setGoldPrice(initialData.metadata?.buy_price_per_gram ? initialData.metadata.buy_price_per_gram.toString() : '');
        setGoldCurrentPrice(initialData.metadata?.current_price_per_gram ? initialData.metadata.current_price_per_gram.toString() : '');
        setGoldDate(initialData.metadata?.transaction_date || new Date().toISOString().split('T')[0]);
        setGoldCashFlowKind(initialData.metadata?.cash_flow_kind === 'actual_purchase' ? 'actual_purchase' : 'balance_snapshot');
      }

      if (policyToEdit) {
        setLicPolicyName(policyToEdit.policy_name || '');
        setLicPolicyNumber(policyToEdit.policy_number || '');
        setLicSumAssured(policyToEdit.sum_assured ? policyToEdit.sum_assured.toString() : '');
        setLicPremiumAmount(policyToEdit.premium_amount ? policyToEdit.premium_amount.toString() : '');
        setLicPremiumFrequency(policyToEdit.premium_frequency || 'yearly');
        setLicStartDate(policyToEdit.start_date || '');
        setLicNextDueDate(policyToEdit.next_due_date || '');
        setLicMaturityDate(policyToEdit.maturity_date || '');
        setLicPremiumsPaidCount(policyToEdit.premiums_paid_count ? policyToEdit.premiums_paid_count.toString() : '1');
        setLicCurrentValue(policyToEdit.current_value ? policyToEdit.current_value.toString() : '');
        setLicAccruedBonus(policyToEdit.accrued_bonus ? policyToEdit.accrued_bonus.toString() : '');
      } else {
        setLicPolicyName('');
        setLicPolicyNumber('');
        setLicSumAssured('');
        setLicPremiumAmount('');
        setLicPremiumFrequency('yearly');
        setLicStartDate('');
        setLicNextDueDate('');
        setLicMaturityDate('');
        setLicPremiumsPaidCount('1');
        setLicCurrentValue('');
        setLicAccruedBonus('');
      }

      if (propertyToEdit) {
        setRealEstatePropertyName(propertyToEdit.property_name || '');
        setRealEstateLocation(propertyToEdit.location || '');
        setRealEstatePropertyCost(propertyToEdit.property_cost ? propertyToEdit.property_cost.toString() : '');
        setRealEstateCurrentValue(propertyToEdit.current_value ? propertyToEdit.current_value.toString() : '');
        setRealEstateLoanOutstanding(propertyToEdit.loan_outstanding ? propertyToEdit.loan_outstanding.toString() : '');
        setRealEstateRentalIncome(propertyToEdit.rental_income ? propertyToEdit.rental_income.toString() : '');
      } else {
        setRealEstatePropertyName('');
        setRealEstateLocation('');
        setRealEstatePropertyCost('');
        setRealEstateCurrentValue('');
        setRealEstateLoanOutstanding('');
        setRealEstateRentalIncome('');
      }

      if (fdToEdit) {
        setFdName(fdToEdit.fd_name || '');
        setFdBankName(fdToEdit.bank_name || '');
        setFdPrincipal(fdToEdit.principal ? fdToEdit.principal.toString() : '');
        setFdInterestRate(fdToEdit.interest_rate ? fdToEdit.interest_rate.toString() : '');
        setFdStartDate(fdToEdit.start_date || '');
        setFdMaturityDate(fdToEdit.maturity_date || '');
        setFdCurrentValue(fdToEdit.current_value ? fdToEdit.current_value.toString() : '');
      } else {
        setFdName('');
        setFdBankName('');
        setFdPrincipal('');
        setFdInterestRate('');
        setFdStartDate('');
        setFdMaturityDate('');
        setFdCurrentValue('');
      }

      if (bankAccountToEdit) {
        setBankAccountName(bankAccountToEdit.account_name || '');
        setBankName(bankAccountToEdit.bank_name || '');
        setBankAccountType(bankAccountToEdit.account_type || 'savings');
        setBankBalance(bankAccountToEdit.balance ? bankAccountToEdit.balance.toString() : '');
        setBankAccountReference(bankAccountToEdit.account_reference || '');
        setBankLastUpdated(bankAccountToEdit.last_updated || new Date().toISOString().split('T')[0]);
      } else {
        setBankAccountName('');
        setBankName('');
        setBankAccountType('savings');
        setBankBalance('');
        setBankAccountReference('');
        setBankLastUpdated(new Date().toISOString().split('T')[0]);
      }
      setSubmittedMessage(null);
      setError(null);
      setDragActive(false);
    }
  }, [show, mode, policyToEdit, propertyToEdit, fdToEdit, bankAccountToEdit, ppfAccountToEdit, epfMemberToEdit, initialData, assetType]);


  const getModalTitle = () => {
    if (!assetType) return 'Add Asset';
    const names: Record<string, string> = {
      nps: 'National Pension System',
      ppf: 'Public Provident Fund',
      epf: 'Employee Provident Fund',
      gold: 'Gold Holdings',
      lic: 'LIC / Insurance',
      real_estate: 'Real Estate',
      fixed_deposit: 'Fixed Deposit',
      bank_account: 'Bank Account'
    };

    if (assetType === 'epf') {
      const epfPrefix = epfEntryMode === 'import' ? 'Import Passbook' : 'Manual Update';
      if (epfEntryMode === 'manual' && epfMemberToEdit) return `Edit Member - ${names[assetType]}`;
      return `${epfPrefix} - ${names[assetType]}`;
    }
    const prefix = mode === 'import' ? 'Import Statement' : 'Update Balance';
    if (assetType === 'lic') {
      return policyToEdit ? `Edit Policy - ${names[assetType]}` : `Add Policy - ${names[assetType]}`;
    }
    if (assetType === 'real_estate') {
      return propertyToEdit ? `Edit Property - ${names[assetType]}` : `Add Property - ${names[assetType]}`;
    }
    if (assetType === 'fixed_deposit') {
      return fdToEdit ? `Edit FD - ${names[assetType]}` : `Add FD - ${names[assetType]}`;
    }
    if (assetType === 'bank_account') {
      return bankAccountToEdit ? `Edit Account - ${names[assetType]}` : `Add Account - ${names[assetType]}`;
    }
    return `${prefix} - ${names[assetType]}`;

  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSubmittedMessage(null);

    try {
      if (!token) {
        setError('Please sign in again before updating assets.');
        setSubmitting(false);
        return;
      }

      let dataPayload: any = {};
      if (assetType === 'nps') {
        if (!file) {
          setError('Please select or drag in a file first.');
          setSubmitting(false);
          return;
        }
        if (!npsAccountDestination) {
          setError('Select the NPS account this statement belongs to.');
          setSubmitting(false);
          return;
        }
        if (npsAccountDestination === '__new__' && (!npsAccountName.trim() || !npsHolderName.trim())) {
          setError('Enter both an account label and family member for the new NPS account.');
          setSubmitting(false);
          return;
        }

        const formData = new FormData();
        formData.append('file', file);
        if (npsAccountDestination !== '__new__') {
          formData.append('account_id', npsAccountDestination);
        } else {
          formData.append('create_new_account', 'true');
        }
        formData.append('account_name', npsAccountName);
        formData.append('holder_name', npsHolderName);
        formData.append('pdf_password', npsPdfPassword);

        const response = await axios.post(`${API_BASE_URL}/assets/import/nps/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Token ${token}`,
          },
        });

        setSubmittedMessage('NPS statement imported and calculated successfully.');
        if (onSubmit) {
          onSubmit(response.data);
        }
      } else if (assetType === 'epf') {
        if (epfEntryMode === 'import') {
          if (!file) {
            setError('Please select or drag in a passbook PDF first.');
            setSubmitting(false);
            return;
          }

          const formData = new FormData();
          formData.append('file', file);
          const response = await axios.post(`${API_BASE_URL}/assets/import/epf/`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Token ${token}`,
            },
          });

          setSubmittedMessage('EPF passbook imported successfully.');
          if (onSubmit) {
            onSubmit(response.data);
          }
          return;
        }

        dataPayload = {
          action: epfMemberToEdit ? 'edit' : 'add',
          id: epfMemberToEdit ? epfMemberToEdit.id : undefined,
          member_name: epfMemberName,
          uan: epfUan,
          member_id: epfMemberId,
          employee_share: parseFloat(epfEmployeeBalance || '0'),
          employer_share: parseFloat(epfEmployerBalance || '0'),
          pension_share: parseFloat(epfPensionBalance || '0'),
          interest_rate: parseFloat(epfInterest),
          monthly_contribution: parseFloat(epfContribution || '0'),
          valuation_date: epfBalanceDate
        };
        const response = await axios.post(`${API_BASE_URL}/assets/epf/manual/`, dataPayload, {
          headers: { Authorization: `Token ${token}` },
        });
        if (onSubmit) {
          onSubmit(response.data);
        }
        setSubmittedMessage(epfMemberToEdit ? 'EPF member updated successfully.' : 'EPF member added successfully.');
      } else if (assetType === 'ppf') {
        dataPayload = {
          action: ppfAccountToEdit ? 'edit' : 'add',
          id: ppfAccountToEdit ? ppfAccountToEdit.id : undefined,
          account_name: ppfAccountName,
          holder_name: ppfHolderName,
          account_reference: ppfAccountReference,
          provider: ppfProvider,
          current_balance: parseFloat(ppfBalance),
          interest_rate: parseFloat(ppfInterest),
          monthly_contribution: parseFloat(ppfContribution || '0'),
          valuation_date: ppfBalanceDate
        };
        const response = await axios.post(`${API_BASE_URL}/assets/ppf/manual/`, dataPayload, {
          headers: { Authorization: `Token ${token}` },
        });
        if (onSubmit) {
          onSubmit(response.data);
        }
        setSubmittedMessage(ppfAccountToEdit ? 'PPF account updated successfully.' : 'PPF account added successfully.');
      } else if (assetType === 'gold') {
        dataPayload = {
          holding_type: goldHoldingType,
          grams: parseFloat(goldGrams),
          price_per_gram: parseFloat(goldPrice),
          current_price_per_gram: parseFloat(goldCurrentPrice || goldPrice),
          transaction_date: goldDate,
          cash_flow_kind: goldCashFlowKind
        };
        const response = await axios.post(`${API_BASE_URL}/assets/gold/manual/`, dataPayload, {
          headers: { Authorization: `Token ${token}` },
        });
        if (onSubmit) {
          onSubmit(response.data);
        }
        setSubmittedMessage('Gold holding saved successfully.');
      } else if (assetType === 'lic') {
        const premium = parseFloat(licPremiumAmount || '0');
        const count = parseInt(licPremiumsPaidCount || '1');
        const bonus = parseFloat(licAccruedBonus || '0');
        
        dataPayload = {
          action: policyToEdit ? 'edit' : 'add',
          id: policyToEdit ? policyToEdit.id : undefined,
          policy_name: licPolicyName,
          policy_number: licPolicyNumber,
          sum_assured: parseFloat(licSumAssured || '0'),
          premium_amount: premium,
          premium_frequency: licPremiumFrequency,
          start_date: licStartDate,
          next_due_date: licNextDueDate,
          maturity_date: licMaturityDate,
          premiums_paid_count: count,
          accrued_bonus: bonus,
          current_value: parseFloat(licCurrentValue || (premium * count + bonus).toString())
        };
        const response = await axios.post(`${API_BASE_URL}/assets/lic/manual/`, dataPayload, {
          headers: { Authorization: `Token ${token}` },
        });
        if (onSubmit) {
          onSubmit(response.data);
        }
        setSubmittedMessage(policyToEdit ? 'Insurance policy updated successfully.' : 'Insurance policy added successfully.');
      } else if (assetType === 'real_estate') {
        dataPayload = {
          action: propertyToEdit ? 'edit' : 'add',
          id: propertyToEdit ? propertyToEdit.id : undefined,
          property_name: realEstatePropertyName,
          location: realEstateLocation,
          property_cost: parseFloat(realEstatePropertyCost || '0'),
          current_value: parseFloat(realEstateCurrentValue || '0'),
          loan_outstanding: parseFloat(realEstateLoanOutstanding || '0'),
          rental_income: parseFloat(realEstateRentalIncome || '0')
        };
        const response = await axios.post(`${API_BASE_URL}/assets/real-estate/manual/`, dataPayload, {
          headers: { Authorization: `Token ${token}` },
        });
        if (onSubmit) {
          onSubmit(response.data);
        }
        setSubmittedMessage(propertyToEdit ? 'Property updated successfully.' : 'Property added successfully.');
      } else if (assetType === 'fixed_deposit') {
        dataPayload = {
          action: fdToEdit ? 'edit' : 'add',
          id: fdToEdit ? fdToEdit.id : undefined,
          fd_name: fdName,
          bank_name: fdBankName,
          principal: parseFloat(fdPrincipal || '0'),
          interest_rate: parseFloat(fdInterestRate || '0'),
          start_date: fdStartDate,
          maturity_date: fdMaturityDate,
          current_value: fdCurrentValue ? parseFloat(fdCurrentValue) : undefined
        };
        const response = await axios.post(`${API_BASE_URL}/assets/fixed-deposit/manual/`, dataPayload, {
          headers: { Authorization: `Token ${token}` },
        });
        if (onSubmit) {
          onSubmit(response.data);
        }
        setSubmittedMessage(fdToEdit ? 'Fixed Deposit updated successfully.' : 'Fixed Deposit added successfully.');
      } else if (assetType === 'bank_account') {
        dataPayload = {
          action: bankAccountToEdit ? 'edit' : 'add',
          id: bankAccountToEdit ? bankAccountToEdit.id : undefined,
          account_name: bankAccountName,
          bank_name: bankName,
          account_type: bankAccountType,
          balance: parseFloat(bankBalance || '0'),
          account_reference: bankAccountReference,
          last_updated: bankLastUpdated
        };
        const response = await axios.post(`${API_BASE_URL}/assets/bank-account/manual/`, dataPayload, {
          headers: { Authorization: `Token ${token}` },
        });
        if (onSubmit) {
          onSubmit(response.data);
        }
        setSubmittedMessage(bankAccountToEdit ? 'Bank account updated successfully.' : 'Bank account added successfully.');
      }
    } catch (err: any) {
      console.error('Error submitting asset:', err);
      const errMsg = err.response?.data?.error || err.message || 'An error occurred during submission.';
      setError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const npsAccounts = Array.isArray(initialData?.metadata?.accounts)
    ? initialData.metadata.accounts
    : [];
  const npsDestinationReady = Boolean(
    npsAccountDestination
    && (
      npsAccountDestination !== '__new__'
      || (npsAccountName.trim() && npsHolderName.trim())
    )
  );

  return (
    <Modal show={show} onHide={onHide} centered className="asset-modal">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold h5">{getModalTitle()}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="pt-3 pb-4">
          {submittedMessage && (
            <Alert variant="info" className="mb-4">
              <strong>Info:</strong> {submittedMessage}
            </Alert>
          )}

          {assetType === 'epf' && (
            <div className="asset-mode-toggle mb-3" role="group" aria-label="EPF update mode">
              <Button
                type="button"
                size="sm"
                variant={epfEntryMode === 'import' ? 'primary' : 'outline-primary'}
                onClick={() => setEpfEntryMode('import')}
              >
                Import passbook
              </Button>
              <Button
                type="button"
                size="sm"
                variant={epfEntryMode === 'manual' ? 'primary' : 'outline-primary'}
                onClick={() => setEpfEntryMode('manual')}
              >
                Manual update
              </Button>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="mb-4">
              <strong>Error:</strong> {error}
            </Alert>
          )}

          {/* NPS Import Form */}
          {assetType === 'nps' && mode === 'import' && (
            <>
              <Row className="g-3 mb-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small">Import statement into</Form.Label>
                    <Form.Select
                      aria-label="NPS destination account"
                      required
                      value={npsAccountDestination}
                      onChange={(e) => {
                        const destination = e.target.value;
                        setNpsAccountDestination(destination);
                        const account = npsAccounts.find((item: any) => item.id === destination);
                        setNpsAccountName(account?.account_name || '');
                        setNpsHolderName(account?.holder_name || '');
                      }}
                    >
                      {npsAccounts.length > 0 && <option value="">Select the correct NPS account</option>}
                      {npsAccounts.map((account: any) => (
                        <option key={account.id} value={account.id}>
                          {account.account_name || account.holder_name || 'NPS account'}
                          {account.holder_name ? ` - ${account.holder_name}` : ''}
                        </option>
                      ))}
                      <option value="__new__">Add new NPS account</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      This choice controls which account receives the statement balance and dated transactions.
                    </Form.Text>
                  </Form.Group>
                </Col>
                {npsAccountDestination && <>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Account Label</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        readOnly={npsAccountDestination !== '__new__'}
                        placeholder="e.g. Dennis NPS"
                        value={npsAccountName}
                        onChange={(e) => setNpsAccountName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Family Member</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        readOnly={npsAccountDestination !== '__new__'}
                        placeholder="e.g. Dennis"
                        value={npsHolderName}
                        onChange={(e) => setNpsHolderName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </>}
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small">PDF Password / PRAN</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter PRAN if the KFintech PDF is protected"
                      value={npsPdfPassword}
                      onChange={(e) => setNpsPdfPassword(e.target.value)}
                      autoComplete="off"
                    />
                    <Form.Text className="text-muted">
                      KFintech NPS PDFs may use your PRAN as the password. This is used only to read the PDF and is not stored.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">NPS Statement File</Form.Label>
                <div
                  className={`nps-dropzone text-center position-relative ${
                    dragActive ? 'nps-dropzone--active' : ''
                  }`}
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-upload-input')?.click()}
                  data-testid="nps-dropzone"
                >
                  <input
                    id="file-upload-input"
                    type="file"
                    className="d-none"
                    accept=".pdf,.csv,.json"
                    onChange={handleFileChange}
                    data-testid="nps-file-input"
                  />
                  
                  {file ? (
                    <div>
                      <div className="fs-2 mb-2">📄</div>
                      <div className="fw-semibold text-truncate mb-1 px-3" style={{ maxWidth: '100%' }}>{file.name}</div>
                      <div className="text-muted small">{(file.size / 1024).toFixed(2)} KB</div>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="text-danger mt-2 p-0" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                      >
                        Remove file
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div className="fs-2 mb-2 text-muted">📤</div>
                      <div className="fw-semibold mb-1">Drag and drop your file here</div>
                      <div className="text-muted small mb-2">or click to browse your computer</div>
                      <div className="text-muted extra-small">
                        Please upload your KFintech or CAMS NPS e-statement (PDF, CSV, or JSON format).
                      </div>
                    </div>
                  )}
                </div>
              </Form.Group>
            </>
          )}

          {/* EPF Import Form */}
          {assetType === 'epf' && epfEntryMode === 'import' && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">EPFO Passbook File</Form.Label>
              <div
                className={`nps-dropzone text-center position-relative ${
                  dragActive ? 'nps-dropzone--active' : ''
                }`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-upload-input-epf')?.click()}
              >
                <input
                  id="file-upload-input-epf"
                  type="file"
                  className="d-none"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
                
                {file ? (
                  <div>
                    <div className="fs-2 mb-2">📄</div>
                    <div className="fw-semibold text-truncate mb-1 px-3" style={{ maxWidth: '100%' }}>{file.name}</div>
                    <div className="text-muted small">{(file.size / 1024).toFixed(2)} KB</div>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="text-danger mt-2 p-0" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      Remove file
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div className="fs-2 mb-2 text-muted">📤</div>
                    <div className="fw-semibold mb-1">Drag and drop your file here</div>
                    <div className="text-muted small mb-2">or click to browse your computer</div>
                    <div className="text-muted extra-small">
                      Please upload your official EPFO member passbook PDF.
                    </div>
                  </div>
                )}
              </div>
            </Form.Group>
          )}

          {/* PPF Manual Form */}
          {assetType === 'ppf' && mode === 'manual' && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Account Label</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Dennis PPF"
                    required
                    value={ppfAccountName}
                    onChange={(e) => setPpfAccountName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Family Member</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Dennis"
                    value={ppfHolderName}
                    onChange={(e) => setPpfHolderName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Provider</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Bank or Post Office"
                    value={ppfProvider}
                    onChange={(e) => setPpfProvider(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Current Balance (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter current balance, e.g. 50000"
                    required
                    min="0"
                    value={ppfBalance}
                    onChange={(e) => setPpfBalance(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Interest Rate (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="Enter current rate"
                    required
                    min="0"
                    value={ppfInterest}
                    onChange={(e) => setPpfInterest(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Monthly Contribution (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 5000"
                    required
                    min="0"
                    value={ppfContribution}
                    onChange={(e) => setPpfContribution(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Account Reference</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Last 4 digits, nickname, or folio reference"
                    value={ppfAccountReference}
                    onChange={(e) => setPpfAccountReference(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Balance Date</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={ppfBalanceDate}
                    onChange={(e) => setPpfBalanceDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* EPF Manual Form */}
          {assetType === 'epf' && epfEntryMode === 'manual' && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Family Member</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Dennis"
                    required
                    value={epfMemberName}
                    onChange={(e) => setEpfMemberName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">UAN</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Optional UAN"
                    value={epfUan}
                    onChange={(e) => setEpfUan(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Member ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Optional member ID"
                    value={epfMemberId}
                    onChange={(e) => setEpfMemberId(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Employee Share Balance (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Employee share balance"
                    min="0"
                    value={epfEmployeeBalance}
                    onChange={(e) => setEpfEmployeeBalance(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Employer Share (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Employer share"
                    min="0"
                    value={epfEmployerBalance}
                    onChange={(e) => setEpfEmployerBalance(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Pension Share (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Pension share"
                    min="0"
                    value={epfPensionBalance}
                    onChange={(e) => setEpfPensionBalance(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Interest Rate (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="Enter current rate"
                    required
                    min="0"
                    value={epfInterest}
                    onChange={(e) => setEpfInterest(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Monthly Contribution (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Employee + employer"
                    required
                    min="0"
                    value={epfContribution}
                    onChange={(e) => setEpfContribution(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Balance Date</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={epfBalanceDate}
                    onChange={(e) => setEpfBalanceDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Gold Manual Form */}
          {assetType === 'gold' && mode === 'manual' && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group controlId="goldHoldingType">
                  <Form.Label className="fw-semibold small">Holding Type</Form.Label>
                  <Form.Select
                    value={goldHoldingType}
                    onChange={(e) => setGoldHoldingType(e.target.value as 'digital' | 'physical')}
                    required
                  >
                    <option value="digital">Digital gold</option>
                    <option value="physical">Physical / in-hand gold</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="goldEntryPurpose">
                  <Form.Label className="fw-semibold small">How should this affect monthly investment?</Form.Label>
                  <Form.Select
                    value={goldCashFlowKind}
                    onChange={(e) => setGoldCashFlowKind(e.target.value as 'balance_snapshot' | 'actual_purchase')}
                    required
                  >
                    <option value="balance_snapshot">Existing holding / balance update (do not count as new money)</option>
                    <option value="actual_purchase">New purchase (count as money invested on this date)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Grams</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    placeholder="Weight in grams, e.g. 10.5"
                    required
                    min="0.001"
                    value={goldGrams}
                    onChange={(e) => setGoldGrams(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Price per Gram (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Buy price per gram"
                    required
                    min="0"
                    value={goldPrice}
                    onChange={(e) => setGoldPrice(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Current Price per Gram (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Current market price"
                    required
                    min="0"
                    value={goldCurrentPrice}
                    onChange={(e) => setGoldCurrentPrice(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    {goldCashFlowKind === 'actual_purchase' ? 'Purchase Date' : 'Balance Date'}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={goldDate}
                    onChange={(e) => setGoldDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* LIC/Insurance Manual Form */}
          {assetType === 'lic' && mode === 'manual' && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Policy Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. LIC Jeevan Anand"
                    required
                    value={licPolicyName}
                    onChange={(e) => setLicPolicyName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Policy Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. 123456789"
                    value={licPolicyNumber}
                    onChange={(e) => setLicPolicyNumber(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Sum Assured (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 500000"
                    min="0"
                    value={licSumAssured}
                    onChange={(e) => setLicSumAssured(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Premium Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Premium paid per installment"
                    required
                    min="0"
                    value={licPremiumAmount}
                    onChange={(e) => setLicPremiumAmount(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Premium Frequency</Form.Label>
                  <Form.Select
                    value={licPremiumFrequency}
                    onChange={(e) => setLicPremiumFrequency(e.target.value)}
                    required
                  >
                    <option value="yearly">Yearly</option>
                    <option value="half-yearly">Half-Yearly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="monthly">Monthly</option>
                    <option value="single">Single Premium</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Premiums Paid Count</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Number of premiums paid"
                    required
                    min="1"
                    value={licPremiumsPaidCount}
                    onChange={(e) => setLicPremiumsPaidCount(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Accrued Bonus (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 15000"
                    min="0"
                    value={licAccruedBonus}
                    onChange={(e) => setLicAccruedBonus(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Current/Surrender Value (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Defaults to premiums paid + bonus"
                    min="0"
                    value={licCurrentValue}
                    onChange={(e) => setLicCurrentValue(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={licStartDate}
                    onChange={(e) => setLicStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Next Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={licNextDueDate}
                    onChange={(e) => setLicNextDueDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Maturity Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={licMaturityDate}
                    onChange={(e) => setLicMaturityDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Real Estate Manual Form */}
          {assetType === 'real_estate' && mode === 'manual' && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Property Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Ocean View Villa"
                    required
                    value={realEstatePropertyName}
                    onChange={(e) => setRealEstatePropertyName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Location / Address</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Flat 302, Sunrise Apts, Bangalore"
                    value={realEstateLocation}
                    onChange={(e) => setRealEstateLocation(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Property Cost / Purchase Price (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Total invested cost"
                    min="0"
                    value={realEstatePropertyCost}
                    onChange={(e) => setRealEstatePropertyCost(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Current Market Value (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Current property valuation"
                    min="0"
                    value={realEstateCurrentValue}
                    onChange={(e) => setRealEstateCurrentValue(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Outstanding Loan Balance (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Remaining loan amount"
                    min="0"
                    value={realEstateLoanOutstanding}
                    onChange={(e) => setRealEstateLoanOutstanding(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Monthly Rental Income (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Rent received per month"
                    min="0"
                    value={realEstateRentalIncome}
                    onChange={(e) => setRealEstateRentalIncome(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Fixed Deposit Manual Form */}
          {assetType === 'fixed_deposit' && mode === 'manual' && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">FD Name / Label</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. SBI FD - 2yr"
                    required
                    value={fdName}
                    onChange={(e) => setFdName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. State Bank of India"
                    value={fdBankName}
                    onChange={(e) => setFdBankName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Principal Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 100000"
                    required
                    min="0"
                    value={fdPrincipal}
                    onChange={(e) => setFdPrincipal(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Interest Rate (% p.a.)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="e.g. 7.1"
                    required
                    min="0"
                    value={fdInterestRate}
                    onChange={(e) => setFdInterestRate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={fdStartDate}
                    onChange={(e) => setFdStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Maturity Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={fdMaturityDate}
                    onChange={(e) => setFdMaturityDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Current Value (₹) — Optional</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Defaults to auto-calculated (principal + simple interest)"
                    min="0"
                    value={fdCurrentValue}
                    onChange={(e) => setFdCurrentValue(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Leave blank to auto-calculate using simple interest: Principal × Rate × Days/365
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Bank Account Manual Form */}
          {assetType === 'bank_account' && mode === 'manual' && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Account Label</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. HDFC Salary Account"
                    required
                    value={bankAccountName}
                    onChange={(e) => setBankAccountName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. HDFC Bank"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Account Type</Form.Label>
                  <Form.Select
                    value={bankAccountType}
                    onChange={(e) => setBankAccountType(e.target.value)}
                    required
                  >
                    <option value="savings">Savings</option>
                    <option value="salary">Salary</option>
                    <option value="current">Current</option>
                    <option value="nre">NRE</option>
                    <option value="nro">NRO</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Current Balance (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 250000"
                    required
                    min="0"
                    value={bankBalance}
                    onChange={(e) => setBankBalance(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Account Reference</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Last 4 digits or nickname"
                    value={bankAccountReference}
                    onChange={(e) => setBankAccountReference(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Balance Date</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={bankLastUpdated}
                    onChange={(e) => setBankLastUpdated(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 justify-content-center">
          <Button variant="secondary" className="px-4" style={{ borderRadius: '14px' }} onClick={onHide}>
            {submittedMessage ? 'Close' : 'Cancel'}
          </Button>
          {!submittedMessage && (
            <Button
              variant="primary"
              type="submit"
              className="px-4"
              style={{ borderRadius: '14px' }}
              disabled={submitting || (assetType === 'nps' && (!file || !npsDestinationReady)) || (assetType === 'epf' && epfEntryMode === 'import' && !file)}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
